import React from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { FaSave, FaCalculator, FaChevronRight, FaHotel, FaBus, FaTicketAlt, FaPlusSquare } from "react-icons/fa";
import { Field } from "@progress/kendo-react-form";
import ShadowCard from "../../../components/common/ShadowCard";
import IconComponent from "../../../components/common/IconComponent";
import FormNumericTextField from "../../../components/formFields/FormNumericTextField";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import "../b2bmodule.scss";

interface PricingAndSaveSectionProps {
    values: any;
    valueGetter: (field: string) => any;
    onFormChange?: (name: string, event: any) => void;
    packageTotals: any[];
}

const BreakdownRow = ({ icon: Icon, label, amount, color = "text-muted" }: any) => (
    <div className="d-flex justify-content-between align-items-center mb-2 px-1">
        <div className="d-flex align-items-center gap-2">
            <div className={`p-1 rounded bg-light ${color}`} style={{ opacity: 0.8 }}>
                <IconComponent icon={Icon} style={{ fontSize: "12px" }} />
            </div>
            <span className="small text-secondary fw-medium">{label}</span>
        </div>
        <span className="small fw-bold text-dark">
            ₹ {(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
    </div>
);

const OptionPricingCard = ({ idx, opt, totals, valueGetter }: any) => {
    const markupType = valueGetter(`PackageOptions[${idx}].MarkupType`) || "Amount";

    return (
        <div className="col-12 col-md-6 col-lg-4 mb-4">
            <div className="pricing-option-card h-100 border rounded-4 overflow-hidden shadow-sm bg-white hover-shadow-lg transition-all">
                {/* Header */}
                <div className="p-3 border-bottom bg-light d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold small" style={{ width: "24px", height: "24px" }}>
                            {idx + 1}
                        </div>
                        <h6 className="fw-bold m-0 text-dark truncate-1" title={opt.OptionName}>
                            {opt.OptionName || `Option ${idx + 1}`}
                        </h6>
                    </div>
                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-2 py-1 small fw-bold">
                        Selling
                    </span>
                </div>

                {/* Body: Cost Breakdown */}
                <div className="p-4" style={{ backgroundColor: "#fafbfc" }}>
                    <div className="mb-4">
                        <div className="section-divider-small mb-3">
                            <span className="bg-light px-2 text-uppercase text-muted fw-bold" style={{ fontSize: "9px", letterSpacing: "1px" }}>Total Cost Breakdown</span>
                        </div>

                        <BreakdownRow icon={FaHotel} label="Accommodations" amount={totals.AccommodationsTotal} color="text-info" />
                        <BreakdownRow icon={FaBus} label="Transports" amount={totals.TransportsTotal} color="text-primary" />
                        <BreakdownRow icon={FaTicketAlt} label="Activities & Tickets" amount={totals.ActivityTotal} color="text-success" />
                        <BreakdownRow icon={FaPlusSquare} label="Extra Services" amount={totals.ExtraServicesTotal} color="text-warning" />

                        <div className="mt-3 pt-3 border-top border-2 border-dashed border-light d-flex justify-content-between align-items-center">
                            <span className="small fw-bold text-secondary">Total Net Cost</span>
                            <span className="fw-bold text-dark h6 m-0">₹ {(totals.OverallTotal || 0).toLocaleString()}</span>
                        </div>
                    </div>

                    {/* Footer: Markup Controls */}
                    <div className="markup-controls-section p-3 rounded-4" style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%)", border: "1px solid #e9ecef" }}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <IconComponent icon={FaCalculator} className="text-primary opacity-75" style={{ fontSize: "14px" }} />
                            <span className="fw-bold text-dark small">Markup & Rounding</span>
                        </div>

                        <div className="row g-2 mb-2">
                            <div className="col-6">
                                {/* ✅ Binding to PackageOptions[idx].MarkupType */}
                                <Field
                                    name={`PackageOptions[${idx}].MarkupType`}
                                    label="Markup Type"
                                    options={[{ label: "Fixed Amount", value: "Amount" }, { label: "Percentage %", value: "Percentage" }]}
                                    component={FormSelectionField}
                                />
                            </div>
                            <div className="col-6">
                                {/* ✅ Binding to PackageOptions[idx].MarkupValue */}
                                <Field
                                    name={`PackageOptions[${idx}].MarkupValue`}
                                    label={markupType === "Percentage" ? "Markup %" : "Markup Value"}
                                    component={FormNumericTextField}
                                />
                            </div>
                        </div>

                        <div className="mb-0">
                            <label className="k-label mb-1" style={{ fontSize: "12px", color: "#656565" }}>Markup Amount (INR)</label>
                            <NumericTextBox
                                value={totals.MarkupAmount}
                                disabled={true}
                                format="n2"
                                size="medium"
                                className="w-100"
                            />
                        </div>
                    </div>
                </div>

                {/* Grand Total Footer */}
                <div className="p-3 text-white border-top border-primary d-flex justify-content-between align-items-center" style={{ background: "linear-gradient(90deg, #1a3a5f 0%, #4267b2 100%)" }}>
                    <div>
                        <div className="text-white-50 small text-uppercase fw-bold m-0" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Grand Total (Per Person)</div>
                        <div className="h4 m-0 fw-bold animate-pulse">₹ {Math.round(totals.GrandTotal || 0).toLocaleString()}</div>
                    </div>
                    <div className="text-white bg-white bg-opacity-25 p-2 rounded-3 border border-white border-opacity-25 shadow-sm">
                        <IconComponent icon={FaChevronRight} style={{ fontSize: "12px" }} />
                    </div>
                </div>
            </div>
        </div>
    );
};

const PricingAndSaveSection: React.FC<PricingAndSaveSectionProps> = ({
    values, valueGetter, packageTotals
}) => {
    const navigate = useNavigate();
    return (
        <React.Fragment>
            <ShadowCard className="b2b-quotation-module b2b-card mb-4 p-0">
                <div className="p-4 d-flex gap-3 align-items-start border-bottom border-light">
                    <div className="hotel-icon-wrap shadow-sm">
                        <IconComponent icon={FaCalculator} />
                    </div>
                    <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2">
                            <Typography.h5 className="section-title mb-0">Final Package Pricing</Typography.h5>
                        </div>
                        <Typography.p className="section-subtitle mb-0 mt-1" style={{ fontSize: "13px" }}>
                            Set your markup and rounding for each package option to calculate the final customer price.
                        </Typography.p>
                    </div>
                </div>

                <div className="p-4">

                    <div className="row g-4 mb-2 mt-2">
                        {(values.PackageOptions || []).map((opt: any, idx: number) => (
                            <OptionPricingCard
                                key={idx}
                                idx={idx}
                                opt={opt}
                                totals={packageTotals[idx] || {}}
                                valueGetter={valueGetter}
                            />
                        ))}
                    </div>

                    <div className="d-flex gap-3 mt-4 pt-4 border-top justify-content-end">
                        <Button
                            type="button"
                            fillMode="outline"
                            themeColor="primary"
                            className="px-4 fw-bold"
                            onClick={() => navigate("/b2bquotation")}
                        >
                            <IconComponent icon={FaChevronRight} style={{ fontSize: "12px", transform: "rotate(180deg)", marginRight: "8px" }} /> Back
                        </Button>

                        <Button
                            type="submit"
                            themeColor="primary"
                            fillMode="solid"
                            className="px-5 fw-bold"
                        >
                            <IconComponent icon={FaSave} className="me-2" /> Save Quotation
                        </Button>
                    </div>
                </div>
            </ShadowCard>
        </React.Fragment>
    );
};

export default PricingAndSaveSection;